import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AchterdeurOptionsPanel from '../components/AchterdeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  raamtypes, 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING 
} from '../data/mockData';
import {
  applyDoorLayoutFieldChange,
} from '../lib/partWidthDistribution';
import {
  applyDesignTypeChange,
  applyTopLightChange,
  VOORDEUR_DOOR_LEAF_MM,
  VOORDEUR_TOP_LIGHT_HEIGHT_MM,
  VOORDEUR_SIDE_PANEL_MM,
  DOOR_NO_PANEL_MAX_WIDTH,
  ACHTERDEUR_MAX_TOTAL_WIDTH,
  DOOR_MAX_DOOR_LEAF_WIDTH,
  DOOR_MIN_DOOR_HEIGHT_MM,
  DOOR_NORMAL_MAX_HEIGHT,
  DOOR_MAX_DOOR_HEIGHT,
  DOOR_MIN_OVERHEAD_HEIGHT,
  DOOR_MAX_TOTAL_HEIGHT,
  getDoorMinTotalWidth,
  enforceDoorWidthBounds,
  enforceDoorHeightBounds,
  applyAchterdeurTwoPartsSandwichSync,
  clearAchterdeurTwoPartsSideParapets,
  applyDoorSidePanelParapetSync,
  applyTerrasdeurSidePanelOverheadSync,
  SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM,
  syncSidePanelPartsForWidth,
} from '../lib/voordeurConfigHelpers';
import {
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import {
  applyPersistedConfiguratorPreferences,
  trackSharedConfiguratorChange,
} from '../lib/configuratorGlazing';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const ACHTERDEUR_MIN_SIDE_PANEL_WIDTH = VOORDEUR_SIDE_PANEL_MM;
const ACHTERDEUR_MIN_DOOR_LEAF_WIDTH = 700;
const ACHTERDEUR_MIN_TOTAL_HEIGHT = DOOR_MIN_DOOR_HEIGHT_MM;

const DEFAULT_ACHTERDEUR_CONFIG = {
  productType: 'achterdeur',
  width: 1000,
  height: 2200,
  profileSystem: 'schuco',
  thresholdHeight: '30mm',
  handlePosition: 'left',
  topLight: 'no-top-light',
  numberOfParts: 'one-part',
  panelType: 'sandwich-no-mullions',
  glassTexture: 'helder-glas',
  glazingType: 'double',
  security: 'skg-2',
  interiorColor: 'ral-9016',
  exteriorColor: 'anthracite',
  draaideelColor: 'anthracite',
  overheadParts: 1,
  overheadPartWindowTypes: ['fixed-sash'],
  ventilatorOverheadParts: [false],
  overheadHeight: 300,
  parapet: 'no-parapet',
  parapetHeight: 250,
  designType: 'no-panel',
  leftPanelWidth: undefined,
  rightPanelWidth: undefined,
  leftPanelWindowType: 'fixed-sash',
  rightPanelWindowType: 'fixed-sash',
  leftPanelParts: 1,
  rightPanelParts: 1,
  leftPanelPartWidths: undefined,
  rightPanelPartWidths: undefined,
  leftPanelPartWindowTypes: ['fixed-sash'],
  rightPanelPartWindowTypes: ['fixed-sash'],
  leftPanelExtendDividersIntoParapet: false,
  rightPanelExtendDividersIntoParapet: false,
  leftPanelVentilator: false,
  rightPanelVentilator: false,
  leftPanelColor: 'ral-7016',
  rightPanelColor: 'ral-7016',
  leftPanelParapet: false,
  rightPanelParapet: false,
  leftPanelParapetHeight: 250,
  rightPanelParapetHeight: 250,
  leftPanelParapetColor: undefined,
  rightPanelParapetColor: undefined,
  leftPanelParapetPanelType: undefined,
  rightPanelParapetPanelType: undefined,
  leftPanelOverhead: false,
  rightPanelOverhead: false,
  leftPanelOverheadWindowType: 'fixed-sash',
  rightPanelOverheadWindowType: 'fixed-sash',
  leftPanelOverheadVentilator: false,
  rightPanelOverheadVentilator: false,
  leftPanelOverheadHeight: 300,
  rightPanelOverheadHeight: 300,
  woodJoint: 'joint-45',
  quantity: 1
};

const Achterdeur = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems(
      applyPersistedConfiguratorPreferences(DEFAULT_ACHTERDEUR_CONFIG, DOOR_COLOR_FIELDS)
    );

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) => withDefaultAdditionalItems(loaded)
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const finalizeAchterdeurUpdate = (next) =>
    next.numberOfParts === 'two-parts'
      ? applyAchterdeurTwoPartsSandwichSync(next)
      : next;

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    trackSharedConfiguratorChange(field, value);
    markDirty();
    setConfig((prev) => {
      if (field === 'designType') {
        const next = enforceDoorWidthBounds({ ...prev, ...applyDesignTypeChange(prev, value) });
        return finalizeAchterdeurUpdate(
          applyDoorSidePanelParapetSync(applyTerrasdeurSidePanelOverheadSync(next))
        );
      }

      if (field === 'topLight') {
        const next = { ...prev, ...applyTopLightChange(prev, value) };
        return finalizeAchterdeurUpdate(next);
      }

      if (field === 'leftPanelParts') {
        return finalizeAchterdeurUpdate({
          ...prev,
          ...syncSidePanelPartsForWidth(prev, 'left', prev.leftPanelWidth, value),
        });
      }
      if (field === 'rightPanelParts') {
        return finalizeAchterdeurUpdate({
          ...prev,
          ...syncSidePanelPartsForWidth(prev, 'right', prev.rightPanelWidth, value),
        });
      }

      if (field === 'overheadParts') {
        const parts = Math.max(1, Number(value) || 1);
        const types = [...(prev.overheadPartWindowTypes || [])];
        const vents = [...(prev.ventilatorOverheadParts || [])];
        while (types.length < parts) types.push('fixed-sash');
        while (vents.length < parts) vents.push(false);
        return finalizeAchterdeurUpdate({
          ...prev,
          overheadParts: parts,
          overheadPartWindowTypes: types.slice(0, parts),
          ventilatorOverheadParts: vents.slice(0, parts),
        });
      }

      if (field === 'width') {
        const numericWidth = Math.round(Number(value) || prev.width);
        const currentDesign = prev.designType || 'no-panel';
        if (
          numericWidth > DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign === 'no-panel'
        ) {
          return finalizeAchterdeurUpdate(
            enforceDoorWidthBounds({
              ...prev,
              designType: 'left-panel',
              width: numericWidth,
              leftPanelWidth: Math.max(
                ACHTERDEUR_MIN_SIDE_PANEL_WIDTH,
                numericWidth - VOORDEUR_DOOR_LEAF_MM
              ),
              rightPanelWidth: undefined,
            })
          );
        }
        if (
          numericWidth <= DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign !== 'no-panel'
        ) {
          return finalizeAchterdeurUpdate({
            ...prev,
            designType: 'no-panel',
            width: numericWidth,
            leftPanelWidth: undefined,
            rightPanelWidth: undefined,
          });
        }
      }

      if (field === 'height') {
        const numericHeight = Math.round(Number(value) || prev.height);
        let heightUpdate;
        if (numericHeight > DOOR_NORMAL_MAX_HEIGHT) {
          heightUpdate = enforceDoorHeightBounds({
            ...prev,
            topLight: 'with-top-light',
            height: numericHeight,
            overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
          });
        } else if (prev.topLight === 'with-top-light') {
          heightUpdate = {
            ...prev,
            topLight: 'no-top-light',
            height: numericHeight,
          };
        } else {
          heightUpdate = { ...prev, height: numericHeight };
        }
        return finalizeAchterdeurUpdate(heightUpdate);
      }

      if (field === 'overheadHeight') {
        const next = enforceDoorHeightBounds({
          ...prev,
          overheadHeight: Math.round(Number(value) || prev.overheadHeight || VOORDEUR_TOP_LIGHT_HEIGHT_MM),
        });
        return finalizeAchterdeurUpdate(next);
      }

      const layoutUpdate = applyDoorLayoutFieldChange(prev, field, value);
      if (layoutUpdate) {
        return finalizeAchterdeurUpdate(
          enforceDoorWidthBounds({ ...prev, ...layoutUpdate })
        );
      }

      if (field === 'numberOfParts') {
        if (value === 'two-parts') {
          return applyAchterdeurTwoPartsSandwichSync({
            ...prev,
            numberOfParts: value,
            panelType: 'sandwich-no-mullions',
            leftPanelParapet: false,
            rightPanelParapet: false,
          });
        }
        return clearAchterdeurTwoPartsSideParapets({
          ...prev,
          numberOfParts: value,
          panelType: 'glass',
        });
      }

      if (prev.numberOfParts === 'two-parts') {
        if (field === 'parapetHeight') {
          return applyAchterdeurTwoPartsSandwichSync(prev, {
            parapetHeight: Math.round(Number(value) || prev.parapetHeight || 250),
          });
        }
        if (field === 'panelType') {
          return applyAchterdeurTwoPartsSandwichSync(prev, { panelType: value });
        }
      }

      if (
        field === 'leftPanelParapet' ||
        field === 'rightPanelParapet' ||
        field === 'leftPanelParapetHeight' ||
        field === 'rightPanelParapetHeight' ||
        field === 'leftPanelParapetPanelType' ||
        field === 'rightPanelParapetPanelType' ||
        field === 'leftPanelParapetColor' ||
        field === 'rightPanelParapetColor' ||
        field === 'leftPanelExtendDividersIntoParapet' ||
        field === 'rightPanelExtendDividersIntoParapet'
      ) {
        if (field === 'leftPanelParapetHeight' || field === 'rightPanelParapetHeight') {
          const parapetHeight = Math.round(Number(value) || prev[field] || 250);
          return finalizeAchterdeurUpdate(
            applyDoorSidePanelParapetSync(prev, {
              leftPanelParapetHeight: parapetHeight,
              rightPanelParapetHeight: parapetHeight,
            })
          );
        }
        if (field === 'leftPanelParapetPanelType' || field === 'rightPanelParapetPanelType') {
          return finalizeAchterdeurUpdate(
            applyDoorSidePanelParapetSync(prev, {
              leftPanelParapetPanelType: value,
              rightPanelParapetPanelType: value,
            })
          );
        }
        if (field === 'leftPanelParapetColor' || field === 'rightPanelParapetColor') {
          return finalizeAchterdeurUpdate(
            applyDoorSidePanelParapetSync(prev, {
              leftPanelParapetColor: value,
              rightPanelParapetColor: value,
            })
          );
        }
        if (
          field === 'leftPanelExtendDividersIntoParapet' ||
          field === 'rightPanelExtendDividersIntoParapet'
        ) {
          return finalizeAchterdeurUpdate(
            applyDoorSidePanelParapetSync(prev, {
              leftPanelExtendDividersIntoParapet: value,
              rightPanelExtendDividersIntoParapet: value,
            })
          );
        }
        return finalizeAchterdeurUpdate(
          applyDoorSidePanelParapetSync({ ...prev, [field]: value })
        );
      }

      if (field === 'leftPanelWindowType' || field === 'rightPanelWindowType') {
        const typesKey =
          field === 'leftPanelWindowType'
            ? 'leftPanelPartWindowTypes'
            : 'rightPanelPartWindowTypes';
        const partsKey =
          field === 'leftPanelWindowType' ? 'leftPanelParts' : 'rightPanelParts';
        const parts = Math.max(1, Number(prev[partsKey]) || 1);
        const prevTypes = Array.isArray(prev[typesKey]) ? prev[typesKey] : [];
        const nextTypes = Array.from(
          { length: parts },
          (_, i) => (parts === 1 ? value : prevTypes[i] || value)
        );
        if (parts === 1) nextTypes[0] = value;
        return finalizeAchterdeurUpdate({
          ...prev,
          [field]: value,
          [typesKey]: nextTypes,
        });
      }

      if (field === 'leftPanelPartWindowTypes' || field === 'rightPanelPartWindowTypes') {
        const singleKey =
          field === 'leftPanelPartWindowTypes'
            ? 'leftPanelWindowType'
            : 'rightPanelWindowType';
        const types = Array.isArray(value) && value.length > 0 ? value : ['fixed-sash'];
        return finalizeAchterdeurUpdate({
          ...prev,
          [field]: types,
          [singleKey]: types[0] || prev[singleKey] || 'fixed-sash',
        });
      }

      if (
        field === 'leftPanelOverhead' ||
        field === 'rightPanelOverhead' ||
        field === 'leftPanelOverheadHeight' ||
        field === 'rightPanelOverheadHeight'
      ) {
        if (field === 'leftPanelOverheadHeight' || field === 'rightPanelOverheadHeight') {
          const overheadHeight = Math.min(
            SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM,
            Math.round(Number(value) || prev[field] || VOORDEUR_TOP_LIGHT_HEIGHT_MM)
          );
          return finalizeAchterdeurUpdate(
            applyTerrasdeurSidePanelOverheadSync(prev, {
              leftPanelOverheadHeight: overheadHeight,
              rightPanelOverheadHeight: overheadHeight,
            })
          );
        }
        return finalizeAchterdeurUpdate(
          applyTerrasdeurSidePanelOverheadSync({ ...prev, [field]: value })
        );
      }

      if (field === 'leftPanelOverheadWindowType' || field === 'rightPanelOverheadWindowType') {
        return finalizeAchterdeurUpdate({ ...prev, [field]: value });
      }

      return finalizeAchterdeurUpdate({ ...prev, [field]: value });
    });
  };

  const handleOverheadPartWindowTypeChange = (index, windowType) => {
    markDirty();
    setConfig(prev => {
      const currentParts = prev.topLight === 'with-top-light' ? (prev.overheadParts || 1) : 1;
      const currentWindowTypes = prev.overheadPartWindowTypes || Array(currentParts).fill('fixed-sash');
      const newWindowTypes = [...currentWindowTypes];
      
      while (newWindowTypes.length <= index) {
        newWindowTypes.push('fixed-sash');
      }
      
      newWindowTypes[index] = windowType;
      const next = {
        ...prev,
        overheadPartWindowTypes: newWindowTypes
      };
      if (windowType !== 'fixed-sash') {
        const vents = [...(prev.ventilatorOverheadParts || [])];
        while (vents.length <= index) vents.push(false);
        vents[index] = false;
        next.ventilatorOverheadParts = vents;
      }
      return next;
    });
  };

  const doorType = config.topLight === 'with-top-light' ? 'top-light' : 'normal';
  const parapet = config.numberOfParts === 'two-parts' ? 'with-parapet' : 'no-parapet';

  return (
    <ConfiguratorShell
      title={t('configurator.backDoorConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <AchterdeurOptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={
        <DoorDrawingBoard
          width={config.width}
          height={config.height}
          designType={config.designType || 'no-panel'}
          doorType={doorType}
          handlePosition={config.handlePosition}
          exteriorColor={config.exteriorColor}
          interiorColor={config.interiorColor}
          draaideelColor={config.draaideelColor || 'anthracite'}
          glassTexture={config.glassTexture}
          woodJoint={config.woodJoint}
          singlePane={true}
          parapet={parapet}
          parapetHeight={config.parapetHeight}
          parapetColor={config.parapetColor}
          panelType={config.panelType}
          overheadPartWindowTypes={config.overheadPartWindowTypes || ['fixed-sash']}
          onOverheadPartWindowTypeChange={handleOverheadPartWindowTypeChange}
          ventilatorOverheadParts={config.ventilatorOverheadParts || [false]}
          onVentilatorOverheadPartsChange={(value) => handleConfigChange('ventilatorOverheadParts', value)}
          overheadHeight={config.overheadHeight}
          onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
          onParapetHeightChange={(value) => handleConfigChange('parapetHeight', value)}
          onWidthChange={(value) => handleConfigChange('width', value)}
          onHeightChange={(value) => handleConfigChange('height', value)}
          leftPanelWidth={config.leftPanelWidth}
          rightPanelWidth={config.rightPanelWidth}
          onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
          onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
          onDoorSectionWidthChange={(value) => handleConfigChange('doorSectionWidth', value)}
          leftPanelWindowType={config.leftPanelWindowType}
          rightPanelWindowType={config.rightPanelWindowType}
          onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
          onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
          leftPanelVentilator={config.leftPanelVentilator}
          rightPanelVentilator={config.rightPanelVentilator}
          onLeftPanelVentilatorChange={(value) => handleConfigChange('leftPanelVentilator', value)}
          onRightPanelVentilatorChange={(value) => handleConfigChange('rightPanelVentilator', value)}
          leftPanelParts={config.leftPanelParts || 1}
          rightPanelParts={config.rightPanelParts || 1}
          leftPanelPartWidths={config.leftPanelPartWidths}
          rightPanelPartWidths={config.rightPanelPartWidths}
          onLeftPanelPartsChange={(value) => handleConfigChange('leftPanelParts', value)}
          onRightPanelPartsChange={(value) => handleConfigChange('rightPanelParts', value)}
          onLeftPanelPartWidthsChange={(value) => handleConfigChange('leftPanelPartWidths', value)}
          onRightPanelPartWidthsChange={(value) => handleConfigChange('rightPanelPartWidths', value)}
          leftPanelPartWindowTypes={config.leftPanelPartWindowTypes}
          rightPanelPartWindowTypes={config.rightPanelPartWindowTypes}
          onLeftPanelPartWindowTypesChange={(value) =>
            handleConfigChange('leftPanelPartWindowTypes', value)
          }
          onRightPanelPartWindowTypesChange={(value) =>
            handleConfigChange('rightPanelPartWindowTypes', value)
          }
          leftPanelExtendDividersIntoParapet={config.leftPanelExtendDividersIntoParapet}
          rightPanelExtendDividersIntoParapet={config.rightPanelExtendDividersIntoParapet}
          onLeftPanelExtendDividersIntoParapetChange={(value) =>
            handleConfigChange('leftPanelExtendDividersIntoParapet', value)
          }
          onRightPanelExtendDividersIntoParapetChange={(value) =>
            handleConfigChange('rightPanelExtendDividersIntoParapet', value)
          }
          leftPanelColor={config.leftPanelColor}
          rightPanelColor={config.rightPanelColor}
          onLeftPanelColorChange={(value) => handleConfigChange('leftPanelColor', value)}
          onRightPanelColorChange={(value) => handleConfigChange('rightPanelColor', value)}
          leftPanelParapet={config.leftPanelParapet}
          rightPanelParapet={config.rightPanelParapet}
          leftPanelParapetHeight={config.leftPanelParapetHeight}
          rightPanelParapetHeight={config.rightPanelParapetHeight}
          leftPanelParapetColor={config.leftPanelParapetColor}
          rightPanelParapetColor={config.rightPanelParapetColor}
          leftPanelParapetPanelType={config.leftPanelParapetPanelType}
          rightPanelParapetPanelType={config.rightPanelParapetPanelType}
          onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
          onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
          onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
          onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
          onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
          onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
          onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
          onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
          sidePanelOverheadEnabled
          leftPanelOverhead={config.leftPanelOverhead}
          rightPanelOverhead={config.rightPanelOverhead}
          leftPanelOverheadWindowType={config.leftPanelOverheadWindowType}
          rightPanelOverheadWindowType={config.rightPanelOverheadWindowType}
          leftPanelOverheadHeight={config.leftPanelOverheadHeight}
          rightPanelOverheadHeight={config.rightPanelOverheadHeight}
          leftPanelOverheadVentilator={config.leftPanelOverheadVentilator}
          rightPanelOverheadVentilator={config.rightPanelOverheadVentilator}
          onLeftPanelOverheadChange={(value) => handleConfigChange('leftPanelOverhead', value)}
          onRightPanelOverheadChange={(value) => handleConfigChange('rightPanelOverhead', value)}
          onLeftPanelOverheadWindowTypeChange={(value) => handleConfigChange('leftPanelOverheadWindowType', value)}
          onRightPanelOverheadWindowTypeChange={(value) => handleConfigChange('rightPanelOverheadWindowType', value)}
          onLeftPanelOverheadHeightChange={(value) => handleConfigChange('leftPanelOverheadHeight', value)}
          onRightPanelOverheadHeightChange={(value) => handleConfigChange('rightPanelOverheadHeight', value)}
          onLeftPanelOverheadVentilatorChange={(value) => handleConfigChange('leftPanelOverheadVentilator', value)}
          onRightPanelOverheadVentilatorChange={(value) => handleConfigChange('rightPanelOverheadVentilator', value)}
          onGlassTextureChange={(value) => handleConfigChange('glassTexture', value)}
          minWidth={getDoorMinTotalWidth(config.designType)}
          maxWidth={ACHTERDEUR_MAX_TOTAL_WIDTH}
          maxDoorLeafWidth={DOOR_MAX_DOOR_LEAF_WIDTH}
          minDoorLeafWidth={ACHTERDEUR_MIN_DOOR_LEAF_WIDTH}
          minSidePanelWidth={ACHTERDEUR_MIN_SIDE_PANEL_WIDTH}
          minHeight={ACHTERDEUR_MIN_TOTAL_HEIGHT}
          maxHeight={DOOR_MAX_TOTAL_HEIGHT}
          maxDoorHeight={DOOR_MAX_DOOR_HEIGHT}
          minDoorHeight={DOOR_MIN_DOOR_HEIGHT_MM}
          minOverheadHeight={DOOR_MIN_OVERHEAD_HEIGHT}
          maxSidePanelOverheadHeight={SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={raamtypes}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="achterdeur"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig, options) => {
            if (options?.resetToDefault) {
              handleReset();
              return;
            }
            resetBaseline(savedConfig || config);
          }}
        />
      }
    />
  );
};

export default Achterdeur;
